export const CORRELATION_ID_HEADER = "X-Correlation-ID";
const CORRELATION_ID_HEADER_LOWERCASE = CORRELATION_ID_HEADER.toLowerCase();

const isHeaderBag = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const readHeaderValue = (args: {
  headers: unknown;
  headerName: string;
}): unknown => {
  if (isHeaderBag(args.headers)) {
    const directMatch = args.headers[args.headerName];
    if (directMatch !== undefined) {
      return directMatch;
    }

    const lowercaseMatch = args.headers[args.headerName.toLowerCase()];
    if (lowercaseMatch !== undefined) {
      return lowercaseMatch;
    }
  }

  if (
    typeof args.headers === "object" &&
    args.headers !== null &&
    "get" in args.headers &&
    typeof args.headers.get === "function"
  ) {
    const headerBag = args.headers as {
      get: (headerName: string) => unknown;
    };
    return (
      headerBag.get.call(headerBag, args.headerName) ??
      headerBag.get.call(headerBag, args.headerName.toLowerCase())
    );
  }

  return undefined;
};

export const readCorrelationIdFromHeaders = (
  headers: unknown,
): string | undefined => {
  // Axios often normalizes browser response headers to lowercase, even when the
  // backend echoes the canonical `X-Correlation-ID` casing.
  const headerValue = readHeaderValue({
    headers,
    headerName: CORRELATION_ID_HEADER,
  });

  if (typeof headerValue === "string") {
    return headerValue;
  }

  if (Array.isArray(headerValue) && typeof headerValue[0] === "string") {
    return headerValue[0];
  }

  const lowercaseHeaderValue = readHeaderValue({
    headers,
    headerName: CORRELATION_ID_HEADER_LOWERCASE,
  });

  if (typeof lowercaseHeaderValue === "string") {
    return lowercaseHeaderValue;
  }

  if (
    Array.isArray(lowercaseHeaderValue) &&
    typeof lowercaseHeaderValue[0] === "string"
  ) {
    return lowercaseHeaderValue[0];
  }

  return undefined;
};

/**
 * Generates a client-side correlation ID for admin -> backend HTTP requests.
 *
 * @remarks
 * The backend echoes `x-correlation-id` in response headers and includes it in request capture logs,
 * so this enables end-to-end correlation across:
 * - HellocashflowAdmin logs
 * - backend request logs (`services/api/logs/telemetry-capture/request-*.log`)
 *
 * This should be stable, safe to log, and unique per request.
 *
 * @see docs/LOGGING.md
 */
export const generateCorrelationId = (): string => {
  const randomUuid =
    typeof globalThis !== "undefined" &&
    "crypto" in globalThis &&
    typeof globalThis.crypto.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : null;

  if (randomUuid) {
    return `hc-admin-${randomUuid}`;
  }

  return `hc-admin-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};
